import { useEffect, useMemo } from "react";

import { shouldOptionBeShown } from "../../../helpers/conditions.ts";
import { useVehicleSelection } from "../../../providers/VehicleSelectionContext.tsx";
import {
  ComplexBundle,
  Product as ProductType,
} from "../../../types/Product.ts";
import DropdownLarge from "../../elements/DropdownLarge.tsx";

interface Props {
  step: ComplexBundle.Step;
  stepIndex: number;
  entry: ComplexBundle.Entry;
  entryIndex: number;
}

export const ProductsFreeTextChecklist = ({
  stepIndex,
  entry,
  entryIndex,
}: Props) => {
  const {
    selectedOptions,
    handleOptionChange,
    selectedGvmUpgrade,
    validationErrors,
  } = useVehicleSelection();

  const hasError = validationErrors.some(
    (err) => err.stepIndex === stepIndex && err.entryIndex === entryIndex,
  );

  const options = useMemo(() => {
    if (!selectedGvmUpgrade) {
      return [];
    }
    return Array.from(entry.products.entries())
      .filter(([, option]) => shouldOptionBeShown(option, selectedOptions))
      .map(([optionIndex, option]) => {
        return {
          id: optionIndex,
          value: optionIndex,
          name: option.option_name,
          price: option.hide_price
            ? null
            : Number(option.product_option.adjusted_price),
          image_url: option.product_option.image_url,
          attributes: null,
          option: option,
          entry: entry,
          optionKey: `${stepIndex}-${entryIndex}-${optionIndex}`,
        } as ProductType;
      });
  }, [
    entry.products,
    entryIndex,
    selectedGvmUpgrade,
    selectedOptions,
    stepIndex,
  ]);

  const entryKey = `${stepIndex}-${entryIndex}`;

  // Selections whose options are no longer visible must be dropped, or they would still
  // be submitted with the cart payload and fail the server price check.
  useEffect(() => {
    const currentlySelected = selectedOptions[entryKey];

    if (!Array.isArray(currentlySelected) || !currentlySelected.length) {
      return;
    }

    const visible = currentlySelected.filter((value) =>
      options.some((opt) => opt.value === value),
    );
    if (visible.length !== currentlySelected.length) {
      handleOptionChange(stepIndex, entryIndex, visible);
    }
  }, [
    handleOptionChange,
    entryIndex,
    options,
    selectedOptions,
    stepIndex,
    entryKey,
  ]);

  return (
    <DropdownLarge
      options={options}
      label={entry.entry_name}
      multiple={true}
      selectedProducts={
        (selectedOptions[entryKey] as number[] | undefined) || []
      }
      required={!!entry.entry_required}
      onChange={(value, isSelected) => {
        let newValues: number[];
        if (value === null) {
          newValues = [];
        } else if (isSelected) {
          newValues = [
            ...((selectedOptions[entryKey] as number[]) || []),
            Number(value),
          ];
        } else {
          newValues = ((selectedOptions[entryKey] as number[]) || []).filter(
            (v) => v !== value,
          );
        }

        handleOptionChange(stepIndex, entryIndex, newValues);
      }}
      error={hasError}
    />
  );
};
